import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { RagChip, Skel, fmt, pct } from "./shared";

const STATUS = { Draft: "text-slate-500", Submitted: "text-slate-500", Verified: "text-emerald-700", SentBack: "text-red-700" };
const openFile = async (id) => {
  try { const r = await api.get(`/files/${id}/download`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
  catch { toast.error("Could not open file"); }
};

export default function IndicatorDrawer({ ind, fy, canEdit, onClose }) {
  const [trend, setTrend] = useState(null);
  const [ents, setEnts] = useState(null);
  useEffect(() => {
    if (!ind) return;
    setTrend(null); setEnts(null);
    api.get(`/mel/indicators/${ind.id}/trend`, { params: { fy } }).then(r => setTrend(r.data)).catch(() => setTrend({ rows: [] }));
    api.get(`/mel/indicators/${ind.id}/entries`).then(r => setEnts(r.data.entries)).catch(() => setEnts([]));
  }, [ind, fy]);
  return (
    <Sheet open={!!ind} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="mon-ind-drawer">
        {ind && <>
          <SheetHeader><SheetTitle className="text-base pr-6">{ind.name}</SheetTitle></SheetHeader>
          <div className="flex flex-wrap gap-4 text-sm mt-2">
            <span className="text-slate-500">Target <b className="text-slate-900 font-mono">{fmt(ind.target)}</b></span>
            <span className="text-slate-500">Actual <b className="text-slate-900 font-mono">{fmt(ind.actual)}</b></span>
            <span className="text-slate-500">% <b className="text-slate-900 font-mono">{pct(ind.pct)}</b></span><RagChip i={ind} />
          </div>
          <div className="flex gap-2 mt-3">
            <a href={`/app/projects/${ind.project_id}?tab=progress`}><Button size="sm" data-testid="mon-drawer-enter">Enter update</Button></a>
            {canEdit && <a href={`/app/projects/${ind.project_id}?tab=indicators`}><Button size="sm" variant="outline" data-testid="mon-drawer-edit">Edit target</Button></a>}
          </div>
          <div className="text-sm font-semibold mt-5 mb-1">Cumulative actual vs target · {fy}</div>
          {!trend ? <Skel rows={1} h="h-48" /> : (
            <div className="h-56" data-testid="mon-drawer-trend"><ResponsiveContainer><LineChart data={trend.rows} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="month" fontSize={10} /><YAxis fontSize={10} /><Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="target_cum" name="Target to date" stroke="#94A3B8" strokeDasharray="4 4" dot={false} />
              <Line type="monotone" dataKey="actual_cum" name="Actual (verified)" stroke="#1F5FA8" strokeWidth={2} />
            </LineChart></ResponsiveContainer></div>)}
          <div className="text-sm font-semibold mt-5 mb-1">Monthly entries</div>
          {!ents ? <Skel rows={3} /> : ents.length === 0 ? <div className="text-xs text-slate-500" data-testid="mon-drawer-noentries">No entries yet.</div> : (
            <table className="w-full text-xs" data-testid="mon-drawer-entries"><thead className="text-slate-500"><tr><th className="text-left py-1">Month</th><th className="text-right">Actual</th><th className="text-left pl-3">Status</th><th className="text-left">Evidence</th></tr></thead>
              <tbody>{ents.map(e => <tr key={e.id} className="border-t">
                <td className="py-1.5 font-mono">{e.period}</td><td className="text-right font-mono">{fmt(e.actual)}</td>
                <td className={`pl-3 ${STATUS[e.status] || ""}`}>{e.status}{e.sent_back_reason ? ` – ${e.sent_back_reason}` : ""}</td>
                <td>{(e.evidence_file_ids || []).map((f, n) => <button key={f} className="text-blue-700 underline mr-2" onClick={() => openFile(f)} data-testid={`mon-drawer-ev-${e.period}-${n}`}>File {n + 1}</button>)}</td>
              </tr>)}</tbody></table>)}
        </>}
      </SheetContent>
    </Sheet>
  );
}
